import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  Download,
  Merge,
  RotateCcw,
  Save,
  Scissors,
  X
} from "lucide-react";
import { api, ApiError, downloadSubtitle } from "../lib/api";
import { formatCueTime } from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { PageLoading } from "../components/Loading";
import type { Cue } from "../types";

export function EditorPage() {
  const { talkId = "" } = useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["talk", talkId],
    queryFn: () => api.talk(talkId),
    enabled: Boolean(talkId)
  });
  useDocumentTitle(query.data ? `编辑 · ${query.data.title}` : "编辑字幕");
  const [cues, setCues] = useState<Cue[]>([]);
  const [dirty, setDirty] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  useEffect(() => {
    if (query.data && dirty.size === 0) setCues(query.data.cues);
  }, [query.data, dirty.size]);

  useEffect(() => {
    function warn(event: BeforeUnloadEvent) {
      if (dirty.size) event.preventDefault();
    }
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty.size]);

  const dirtyCount = dirty.size;
  const averageCue = useMemo(
    () =>
      cues.length
        ? Math.round(
            cues.reduce((total, cue) => total + cue.endMs - cue.startMs, 0) /
              cues.length /
              100
          ) / 10
        : 0,
    [cues]
  );

  if (query.isLoading) return <PageLoading label="正在打开字幕编辑器" />;
  if (query.isError || !query.data) {
    return (
      <div className="page-frame">
        <EmptyState
          eyebrow="无法编辑"
          title="字幕没有加载出来"
          body={
            query.error instanceof ApiError
              ? query.error.message
              : "演讲不存在或服务连接中断。"
          }
          action={<Link to="/" className="button button-secondary">返回学习库</Link>}
        />
      </div>
    );
  }
  const talk = query.data;
  if (talk.status !== "ready") {
    return (
      <div className="page-frame">
        <EmptyState
          eyebrow="还不能编辑"
          title="等待字幕生成完成"
          body="视频处理尚未结束。回到学习页查看进度，完成后即可校对。"
          action={<Link to={`/talks/${talk.id}`} className="button button-primary">查看任务进度</Link>}
        />
      </div>
    );
  }

  function patchLocal(
    cueId: number,
    patch: Partial<Pick<Cue, "startMs" | "endMs" | "en" | "zh">>
  ) {
    setCues((current) =>
      current.map((cue) => (cue.id === cueId ? { ...cue, ...patch } : cue))
    );
    setDirty((current) => new Set(current).add(cueId));
    setMessage("");
  }

  async function saveAll() {
    setBusy(true);
    setMessage("");
    try {
      for (const cue of cues) {
        if (!dirty.has(cue.id)) continue;
        await api.patchCue(talk.id, cue.id, {
          startMs: cue.startMs,
          endMs: cue.endMs,
          en: cue.en,
          zh: cue.zh
        });
      }
      setDirty(new Set());
      await query.refetch();
      setMessage("修改已保存");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "保存失败");
    } finally {
      setBusy(false);
    }
  }

  async function splitCue(cue: Cue) {
    if (dirty.size) {
      setMessage("请先保存修改，再拆分字幕。");
      return;
    }
    try {
      await api.splitCue(talk.id, cue.id);
      await query.refetch();
      setMessage("已从句子中点拆成两条字幕");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "拆分失败");
    }
  }

  async function mergeCue(cue: Cue) {
    if (dirty.size) {
      setMessage("请先保存修改，再合并字幕。");
      return;
    }
    try {
      await api.mergeCue(talk.id, cue.id);
      await query.refetch();
      setMessage("已合并下一句字幕");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "合并失败");
    }
  }

  async function regenerate() {
    setBusy(true);
    try {
      await api.regenerateTalk(talk.id);
      setRegenerateOpen(false);
      window.location.href = `/talks/${talk.id}`;
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "无法重新生成");
      setBusy(false);
    }
  }

  return (
    <div className="editor-page page-frame">
      <div className="editor-topbar">
        <Link to={`/talks/${talk.id}`} className="back-link">
          <ArrowLeft size={15} />
          返回播放页
        </Link>
        <div className="editor-actions">
          <Button
            variant="ghost"
            icon={<Download size={15} />}
            onClick={() => downloadSubtitle(talk, "srt", "bilingual")}
          >
            导出双语 SRT
          </Button>
          <Button
            variant="secondary"
            icon={<RotateCcw size={15} />}
            onClick={() => setRegenerateOpen(true)}
          >
            重新生成
          </Button>
          <Button
            variant="primary"
            icon={<Save size={15} />}
            loading={busy}
            disabled={!dirty.size}
            onClick={saveAll}
          >
            保存修改{dirty.size ? `（${dirty.size}）` : ""}
          </Button>
        </div>
      </div>

      <header className="editor-heading">
        <div>
          <span className="eyebrow">TRANSCRIPT EDITOR</span>
          <h1>校对字幕</h1>
          <p>{talk.title}</p>
          {talk.titleZh && <p className="editor-title-zh">{talk.titleZh}</p>}
        </div>
        <div className="editor-metrics">
          <div>
            <strong>{cues.length}</strong>
            <span>条字幕</span>
          </div>
          <div>
            <strong>{averageCue}s</strong>
            <span>平均时长</span>
          </div>
          <div>
            <strong>{dirtyCount}</strong>
            <span>处待保存</span>
          </div>
        </div>
      </header>

      {message && (
        <div className="editor-message" role="status">
          {message.includes("保存") ? <Check size={15} /> : <X size={15} />}
          {message}
        </div>
      )}

      <div className="editor-list">
        {cues.map((cue, index) => (
          <article
            key={cue.id}
            className={`cue-editor ${dirty.has(cue.id) ? "is-dirty" : ""}`}
          >
            <div className="cue-editor-index">
              <span>{String(index + 1).padStart(3, "0")}</span>
              <i />
            </div>
            <div className="cue-editor-time">
              <label>
                <span>开始</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={(cue.startMs / 1000).toFixed(2)}
                  onChange={(event) =>
                    patchLocal(cue.id, {
                      startMs: Math.max(0, Math.round(Number(event.target.value) * 1000))
                    })
                  }
                />
              </label>
              <label>
                <span>结束</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={(cue.endMs / 1000).toFixed(2)}
                  onChange={(event) =>
                    patchLocal(cue.id, {
                      endMs: Math.max(0, Math.round(Number(event.target.value) * 1000))
                    })
                  }
                />
              </label>
              <small>{formatCueTime(cue.startMs)}</small>
            </div>
            <div className="cue-editor-fields">
              <label>
                <span className="field-label">ENGLISH</span>
                <textarea
                  value={cue.en}
                  rows={2}
                  onChange={(event) => patchLocal(cue.id, { en: event.target.value })}
                />
              </label>
              <label>
                <span className="field-label">中文</span>
                <textarea
                  value={cue.zh}
                  rows={2}
                  onChange={(event) => patchLocal(cue.id, { zh: event.target.value })}
                />
              </label>
            </div>
            <div className="cue-editor-tools">
              <button onClick={() => void splitCue(cue)} title="从句子中点拆分">
                <Scissors size={15} />
                拆分
              </button>
              <button
                onClick={() => void mergeCue(cue)}
                disabled={index === cues.length - 1}
                title="与下一句合并"
              >
                <Merge size={15} />
                合并下句
              </button>
            </div>
          </article>
        ))}
      </div>

      <ConfirmDialog
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
        title="重新生成整篇字幕？"
        description="当前所有人工修改会被清除。云端会重新运行语音识别和翻译，并从断点重试。"
        confirmLabel="清除并重新生成"
        danger
        busy={busy}
        onConfirm={() => void regenerate()}
      />
    </div>
  );
}

